import Phaser from "phaser";
import { Palette } from "../ui/palette";
import { makeButton } from "../ui/widgets";
import { CUTSCENES, SPEAKERS, type Beat, type Cutscene, type CutsceneKey } from "../data/cutscenes";

interface DialogueData {
  /** A named cutscene from src/data/cutscenes.ts ... */
  scriptKey?: CutsceneKey;
  /** ... or an inline script (used for one-offs / tests). */
  script?: Cutscene;
  /** Scene to start once the cutscene ends (or is skipped). */
  next: string;
  /** Data handed to the next scene's create(). */
  nextData?: object;
}

// ---- layout constants (960x540 canvas) ------------------------------------
const W = 960;
const H = 540;
const BOX = { x: 40, y: 356, w: 880, h: 150 }; // bottom dialogue panel
const PAD = 22; // inner text padding
const PORTRAIT_PX = 176; // framed portrait size on screen
const PORTRAIT_Y = 210; // portrait centre (above the box)
const FRAME = 5; // portrait frame thickness
const TYPE_MS = 22; // per-character typewriter delay

/**
 * A reusable old-school dialogue / narration screen. Hand it a cutscene (a list
 * of beats) plus the scene to go to afterwards; it plays the beats one at a time,
 * typing each out, and advances on click / space / enter. See src/data/cutscenes.ts.
 *
 * Narration beats and spoken lines look deliberately different (see the two render
 * paths below): narration is centred, gold and italic with no portrait; a line
 * shows the speaker's framed portrait on their side, a coloured name plate, and
 * their words left-aligned. Long text scrolls inside the box (it auto-scrolls to
 * follow the typewriter, and the mouse wheel scrolls it manually).
 */
export class DialogueScene extends Phaser.Scene {
  private beats: Beat[] = [];
  private index = 0;
  private next!: string;
  private nextData: object | undefined;

  // per-beat objects, torn down between beats
  private beatObjs: Phaser.GameObjects.GameObject[] = [];
  private textObj?: Phaser.GameObjects.Text;
  private textContainer?: Phaser.GameObjects.Container;
  private textMask?: Phaser.Display.Masks.GeometryMask;
  private full = ""; // the beat's full text
  private shown = 0; // characters revealed so far
  private typing = false;
  private typeEvent?: Phaser.Time.TimerEvent;

  // scroll state for the current beat's text
  private scrollY = 0; // container offset (<= 0)
  private scrollMin = 0; // most-negative offset (text height - visible height)
  private visibleH = 0;

  private cue?: Phaser.GameObjects.Text;

  constructor() {
    super("Dialogue");
  }

  create(data: DialogueData) {
    const cs = data.script ?? (data.scriptKey ? CUTSCENES[data.scriptKey] : undefined);
    this.beats = cs?.beats ?? [];
    this.next = data.next;
    this.nextData = data.nextData;
    this.index = 0;

    // Backdrop: a soft vertical wash so the box reads against it.
    this.add.rectangle(W / 2, H / 2, W, H, Palette.bg).setDepth(0);
    this.add.rectangle(W / 2, H / 2 - 40, W, 340, 0x141821, 0.6).setDepth(0);

    // Skip whole cutscene.
    makeButton(this, W - 78, 30, "Skip  ⏭", () => this.finish(), { width: 120, height: 36, fontSize: "14px" }).setDepth(50);

    // Advance on click / space / enter.
    this.input.on("pointerdown", () => this.advance());
    this.input.keyboard?.on("keydown-SPACE", () => this.advance());
    this.input.keyboard?.on("keydown-ENTER", () => this.advance());
    this.input.keyboard?.on("keydown-ESC", () => this.finish());

    // Mouse wheel scrolls the current beat's text when it overflows.
    this.input.on("wheel", (_p: unknown, _o: unknown, _dx: number, dy: number) => this.scrollBy(-dy * 0.6));

    if (this.beats.length === 0) return this.finish();
    this.showBeat();
  }

  // ---- beat lifecycle ------------------------------------------------------

  private clearBeat() {
    this.typeEvent?.remove();
    this.typeEvent = undefined;
    this.beatObjs.forEach((o) => o.destroy());
    this.beatObjs = [];
    this.textObj = undefined;
    this.textContainer = undefined;
    this.textMask?.destroy();
    this.textMask = undefined;
    this.cue?.destroy();
    this.cue = undefined;
    this.scrollY = 0;
    this.scrollMin = 0;
  }

  private showBeat() {
    this.clearBeat();
    const beat = this.beats[this.index];

    // The dialogue panel (shared by both beat kinds).
    const panel = this.add.rectangle(BOX.x, BOX.y, BOX.w, BOX.h, Palette.panel, 0.96).setOrigin(0, 0).setDepth(2);
    panel.setStrokeStyle(3, Palette.panelEdge);
    this.beatObjs.push(panel);

    if (beat.kind === "narration") {
      this.full = beat.text;
      this.buildScrollText({
        x: BOX.x + PAD,
        y: BOX.y + PAD,
        w: BOX.w - PAD * 2,
        h: BOX.h - PAD * 2,
        align: "center",
        color: "#ffd479",
        italic: true,
      });
    } else {
      const sp = SPEAKERS[beat.speaker];
      this.full = beat.text;
      const left = sp.side === "left";

      // Framed portrait on the speaker's side, above the box.
      const px = left ? BOX.x + PORTRAIT_PX / 2 + 10 : BOX.x + BOX.w - PORTRAIT_PX / 2 - 10;
      this.buildPortrait(px, PORTRAIT_Y, sp.tex, sp.color);

      // Name plate: a coloured tab on the top edge of the box, under the portrait.
      this.buildNamePlate(px, BOX.y, sp.name, sp.color);

      // Text fills the box (portrait floats above it, so text can use full width).
      this.buildScrollText({
        x: BOX.x + PAD,
        y: BOX.y + PAD,
        w: BOX.w - PAD * 2,
        h: BOX.h - PAD * 2,
        align: "left",
        color: Palette.text,
        italic: false,
      });
    }

    // Blinking "continue" cue, hidden until the line finishes typing.
    this.cue = this.add
      .text(BOX.x + BOX.w - 16, BOX.y + BOX.h - 14, "▶", { fontFamily: "monospace", fontSize: "18px", color: Palette.textDim })
      .setOrigin(1, 1)
      .setDepth(6)
      .setAlpha(0);

    this.startTyping();
  }

  private buildPortrait(cx: number, cy: number, tex: string, color: number) {
    const outer = PORTRAIT_PX + FRAME * 2;
    const frame = this.add.rectangle(cx, cy, outer, outer, 0x0b0d12, 1).setDepth(3);
    frame.setStrokeStyle(FRAME, color);
    this.beatObjs.push(frame);

    if (this.textures.exists(tex)) {
      const img = this.add.image(cx, cy, tex).setDepth(4);
      img.setDisplaySize(PORTRAIT_PX, PORTRAIT_PX);
      this.beatObjs.push(img);
    }
  }

  private buildNamePlate(cx: number, boxTop: number, name: string, color: number) {
    const label = this.add
      .text(0, 0, name.toUpperCase(), { fontFamily: "monospace", fontSize: "16px", color: "#0b0d12", fontStyle: "bold" })
      .setOrigin(0.5)
      .setDepth(6);
    const w = label.width + 28;
    const h = 26;
    const plate = this.add.rectangle(cx, boxTop, w, h, color, 1).setDepth(5);
    plate.setStrokeStyle(2, 0x0b0d12);
    label.setPosition(cx, boxTop);
    this.beatObjs.push(plate, label);
  }

  /** Build a word-wrapped text object inside a masked, scrollable container. */
  private buildScrollText(o: { x: number; y: number; w: number; h: number; align: string; color: string; italic: boolean }) {
    this.visibleH = o.h;
    const container = this.add.container(o.x, o.y).setDepth(4);
    const txt = this.add.text(0, 0, "", {
      fontFamily: "monospace",
      fontSize: "19px",
      color: o.color,
      align: o.align,
      lineSpacing: 8,
      fontStyle: o.italic ? "italic" : "normal",
      wordWrap: { width: o.w },
      fixedWidth: o.w,
    });
    if (o.align === "center") txt.setAlign("center");
    container.add(txt);

    // Clip the text to the box so overflow scrolls instead of spilling out.
    const g = this.make.graphics({});
    g.fillRect(o.x, o.y, o.w, o.h);
    const mask = g.createGeometryMask();
    container.setMask(mask);

    this.textObj = txt;
    this.textContainer = container;
    this.textMask = mask;
    this.beatObjs.push(container, g);
  }

  // ---- typewriter ----------------------------------------------------------

  private startTyping() {
    this.shown = 0;
    this.typing = true;
    this.renderText();
    this.typeEvent = this.time.addEvent({
      delay: TYPE_MS,
      loop: true,
      callback: () => {
        this.shown++;
        this.renderText();
        if (this.shown >= this.full.length) this.finishTyping();
      },
    });
  }

  private finishTyping() {
    this.typeEvent?.remove();
    this.typeEvent = undefined;
    this.shown = this.full.length;
    this.typing = false;
    this.renderText();
    // Blink the continue cue.
    if (this.cue) {
      this.cue.setAlpha(1);
      this.tweens.add({ targets: this.cue, alpha: 0.2, duration: 600, yoyo: true, repeat: -1 });
    }
  }

  private renderText() {
    if (!this.textObj) return;
    this.textObj.setText(this.full.slice(0, this.shown));
    // Recompute scroll bounds; auto-follow the caret to the bottom while typing.
    const textH = this.textObj.height;
    this.scrollMin = Math.min(0, this.visibleH - textH);
    if (this.typing) this.scrollY = this.scrollMin; // keep the newest line in view
    this.applyScroll();
  }

  private scrollBy(dy: number) {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + dy, this.scrollMin, 0);
    this.applyScroll();
  }

  private applyScroll() {
    if (!this.textContainer || !this.textObj) return;
    this.textContainer.y = BOX.y + PAD + this.scrollY;
  }

  // ---- advance / finish ----------------------------------------------------

  private advance() {
    if (this.typing) {
      // First tap completes the current line instead of skipping it.
      this.finishTyping();
      return;
    }
    this.index++;
    if (this.index >= this.beats.length) return this.finish();
    this.showBeat();
  }

  private finish() {
    this.clearBeat();
    this.scene.start(this.next, this.nextData);
  }
}
